'use client';

import { useLocale } from '@hooks/use-locale';
import { cn } from '@lib/utils';
import MonogramLogo from '@visual/monogram';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa6';

interface WhatsAppFabProps {
  phoneNumber: string;
  isNav?: boolean;
}

const WhatsAppIcon = ({ className = '' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" className={`h-4 w-4 fill-white ${className}`}>
    <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
  </svg>
);

export default function WhatsAppFab({ phoneNumber, isNav = false }: WhatsAppFabProps) {
  const [isOpen, setIsOpen] = useState(false);
  const fabRef = useRef<HTMLDivElement>(null);
  const { locale } = useLocale();
  const isTamil = locale === 'ta';

  // Format the phone number properly for WhatsApp link
  const formattedPhone = phoneNumber.startsWith('+') ? phoneNumber.substring(1) : phoneNumber;

  const whatsappUrl = `https://wa.me/${formattedPhone}`;

  // Toggle chat bubble open/closed state
  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  // Close the chat bubble
  const closeChat = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (fabRef.current && !fabRef.current.contains(event.target as Node)) {
        closeChat();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div
      className={cn(
        'fixed right-8 bottom-20 z-50 hidden flex-col items-end lg:flex',
        isNav ? 'relative right-2 bottom-0 flex' : 'md:bottom-8'
      )}
      ref={fabRef}
    >
      {/* Expanded chat dialog */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            id="whatsapp-help-panel"
            className={cn(
              'border-border bg-card text-card-foreground mb-4 w-[280px] rounded-xl border p-4 shadow-md',
              isNav ? 'absolute bottom-12' : ''
            )}
          >
            <div className="flex items-center gap-2">
              <div className="bg-primary flex h-10 w-10 items-center justify-center rounded-full">
                <MonogramLogo className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="font-medium">
                  {isTamil ? 'வணக்கம்! இன்று எப்படிச் உதவலாம்?' : 'Hello! How can we assist you today?'}
                </p>
              </div>
            </div>

            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 flex min-h-12 items-center justify-center gap-2 rounded-lg bg-[#187e43] px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#116534]"
            >
              {isNav ? <FaWhatsapp className="h-4 w-4" /> : <WhatsAppIcon />}
              {isTamil ? 'எங்களுடன் பேசுங்கள்' : 'Chat with us'}
            </a>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FAB Button */}
      <button
        type="button"
        onClick={toggleChat}
        className={cn(
          'flex h-14 w-14 cursor-pointer items-center justify-center rounded-full bg-[#187e43] text-white shadow-md transition-colors hover:bg-[#116534]',
          isNav ? 'h-11 w-11' : '',
          isOpen ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''
        )}
        aria-expanded={isOpen}
        aria-controls="whatsapp-help-panel"
        aria-label={isOpen ? (isTamil ? 'அரட்டையை மூடு' : 'Close chat') : isTamil ? 'அரட்டையை திற' : 'Open chat'}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={isOpen ? 'x' : 'wa'}
            initial={{ opacity: 0, scale: 0.5, rotate: -45 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            exit={{ opacity: 0, scale: 0.5, rotate: 45 }}
            transition={{ duration: 0.2 }}
          >
            {isOpen ? <X className="h-7 w-7" /> : <WhatsAppIcon className="h-7 w-7 fill-white" />}
          </motion.div>
        </AnimatePresence>
      </button>
    </div>
  );
}
